import { useMemo } from 'react';
import { useQuery } from '@supabase-cache-helpers/postgrest-swr';

import { alpha } from '@mui/material/styles';

import { colorInterpolate, colorInterpolateFromNames } from 'src/utils/color';

import { supabase } from 'src/auth/context/supabase/lib';

// ----------------------------------------------------------------------

// SETUP COLORS

export const grey = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

export const primary = {
  lighter: '#a8bcd7',
  light: '#6d90bc',
  main: '#1d2c3f',
  dark: '#283d57',
  darker: '#0d141d',
  contrastText: '#FFFFFF',
};

export const secondary = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
};

export const info = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

export const success = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff',
};

export const warning = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: grey[800],
};

export const error = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

export function useGetCategoriesColors() {
  const { data, isLoading } = useQuery(
    supabase.from('data_categories').select('id, name, color, is_other')
  );

  const categoriesColors = useMemo(
    () =>
      data?.map((item) => ({
        category: item.name,
        color: item.color,
        is_other: item.is_other,
      })) || [],
    [data]
  );

  return { categoriesColors, isLoading };
}

const defaultCategory = {
  name: 'Autre',
  color: '#602E3A',
};

export function useGetDefaultCategoryColor() {
  const { data, isLoading } = useQuery(
    supabase.from('data_categories').select('id, name, color').eq('is_other', true)
  );

  const category = useMemo(() => {
    if (isLoading || !data) {
      return defaultCategory;
    }

    const otherCategory = data[0];
    if (!otherCategory) {
      return defaultCategory;
    }

    return {
      name: otherCategory?.name || defaultCategory.name,
      color: otherCategory?.color || defaultCategory.color,
    };
  }, [data, isLoading]);

  return { category, isLoading };
}

export function useGetAxesColors() {
  const { data, isLoading } = useQuery(supabase.from('axes').select('id, numero, color'));

  const axesColors = useMemo(
    () =>
      data?.map((item) => ({
        id: item.id,
        numero: item.numero,
        color: item.color,
      })) || [],
    [data]
  );

  return { axesColors, isLoading };
}

export function useFindChartColors(serie, type = 'categories') {
  const { categoriesColors } = useGetCategoriesColors();
  const { axesColors } = useGetAxesColors();

  const colors = useMemo(() => {
    if (type === 'categories') {
      const otherCategory =
        categoriesColors.find((category) => category.is_other) || defaultCategory;
      return serie.map((item) => {
        const foundCategoryColor = categoriesColors.find(
          (categoryColor) => categoryColor.category === item.name
        );
        return foundCategoryColor ? foundCategoryColor.color : otherCategory.color;
      });
    }

    // Pour les axes
    return serie.map((item) => {
      const foundAxisColor = axesColors.find((axisColor) => axisColor.numero === item.numero);
      return foundAxisColor ? foundAxisColor.color : primary.main;
    });
  }, [serie, categoriesColors, axesColors, type]);

  return colors;
}

export const generateChartColors = (length, startColor = primary.main, endColor = error.dark) => {
  const array = [];
  for (let i = 0; i < length; i += 1) {
    array.push(colorInterpolate(i, length, startColor, endColor));
  }
  return array;
};

export function useGenerateInventoriesChartColors(
  serie,
  startColor = primary.main,
  endColor = error.dark
) {
  const colors = useMemo(
    () =>
      serie.map(
        (item, index) => colorInterpolateFromNames(item.name, startColor, endColor) || '#CCCCCC'
      ),
    [serie, startColor, endColor]
  );

  return colors;
}

const base = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: alpha(grey[500], 0.2),
  action,
};

// ----------------------------------------------------------------------

export function palette(mode) {
  const light = {
    ...base,
    mode: 'light',
    text: {
      primary: primary.main,
      secondary: grey[600],
      disabled: primary.light,
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: grey[200],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
  };

  const dark = {
    ...base,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: grey[500],
      disabled: grey[600],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.12),
    },
    action: {
      ...base.action,
      active: grey[500],
    },
  };

  return mode === 'light' ? light : dark;
}
